// Every [T] tunable from docs/04 and docs/10 lives here. Tune with `pnpm balance`, not by feel alone.
import type { PatternId, Rarity, SizeId } from '../sim/types';

export const B = {
  // ── Grid ──────────────────────────────────────────────
  SIZES: {
    bandit: { reels: 3, rows: 1 },
    cabinet: { reels: 3, rows: 3 },
    wide: { reels: 4, rows: 3 },
    grand: { reels: 5, rows: 3 },
    tall: { reels: 5, rows: 4 },
    cathedral: { reels: 5, rows: 5 },
  } satisfies Record<SizeId, { reels: number; rows: number }>,
  PEEK_ROWS: 1,
  START_DECK: [
    ['cherry', 3],
    ['lemon', 3],
    ['coin', 3],
    ['bell', 1],
    ['seven', 1],
  ] as [string, number][],

  // ── Patterns (base MULT) ───────────────────────────────
  PATTERN_MULT: {
    pair: 0.5,
    line3: 1,
    col3: 1,
    diag3: 1.5,
    line4: 2,
    line5: 3,
    vee: 3,
    zig: 2.5,
    eye: 5,
    cross: 4,
    gorge: 10,
    bandit: 3,
  } satisfies Record<PatternId, number>,
  GORGE_MULT_BY_SIZE: { cabinet: 10, wide: 15, grand: 20, tall: 30, cathedral: 50 } as Partial<
    Record<SizeId, number>
  >,
  HYMN_MULT: 0.5,

  // ── Luck ───────────────────────────────────────────────
  PERFECT_PULL_LUCK: 1,
  PERFECT_PULL_MS: 250,
  PERFECT_PULL_TRAVEL: 0.9,
  PITY_AFTER: 3,
  PITY_BASE_LUCK: 3,
  PITY_STEP: 2,
  FATE_CAP: 30,
  LUCK_OVERCAP_XMULT: 0.02,

  // ── Tributes & windows (docs/10 §1) ────────────────────
  TRIBUTE_BASE: 8,
  // Balance pass 2: a flatter Act II curve (the doc's ×2/×2.2 outran every build right after It Wakes).
  TRIBUTE_GROWTH: [
    { upTo: 5, g: 2.0 },
    { upTo: 10, g: 1.8 },
    { upTo: 18, g: 1.6 },
    { upTo: 40, g: 1.9 },
    { upTo: Infinity, g: 2.8 },
  ],
  RECKONING_TRIBUTE_MULT: 1.5,
  SPINS_BASE: 10,
  SPINS_EVERY: 5,
  /**
   * Pay-early bonus per unused spin, as a share of par (an average spin's share of the tribute),
   * capped at PAY_EARLY_CAP × T(n). Balance pass 2: it used to be 10% of the whole tribute per
   * spin, so paying the moment a window opened refunded ≥100% of it and coins compounded forever.
   */
  PAY_EARLY_PCT: 0.3,
  PAY_EARLY_CAP: 0.5,
  START_HOLD: 0,
  START_NUDGE: 0,

  // ── Souls ──────────────────────────────────────────────
  SOUL_BASE: 1.38,
  CONSUMED_BONUS_DIV: 2,

  // ── Web ────────────────────────────────────────────────
  NODE_GROWTH: 1.8,

  // ── Intermission prices (fractions of T(n+1)) ─────────
  DRAFT_CHOICES: 3,
  DRAFT_REROLL: 0.05,
  SHOP_REROLL: 0.08,
  EXTRA_SPIN: 0.15,
  EXTRA_SPIN_MAX: 3,
  EXTRA_CHARGE: 0.1,
  HYMN_PRICE: 0.3,
  CHARM_PRICE: 0.25,
  CHARM_TIER: { C: 1, U: 2, R: 4, L: 8 } satisfies Record<Rarity, number>,
  CHARM_SELL: 0.5,
  CHARM_ODDS: { C: 60, U: 28, R: 10, L: 2 } satisfies Record<Rarity, number>,
  SHOP_CHARMS: 3,
  CHARM_SLOTS_BASE: 2,
  CHARM_SLOTS_MAX: 7,

  // Draft rarity weights: C = 70 − 1.2n, U = 25 + 0.6n, R = 5 + 0.5n, L = max(0, 0.1(n − 10))
  DRAFT_W: { C: [70, -1.2], U: [25, 0.6], R: [5, 0.5], L: [-1, 0.1] } as Record<Rarity, [number, number]>,
  DRAFT_RARITY_BOOST: 0.25,
  SIGIL_DRAFT_WEIGHT: 1,

  // ── Win tiers (× par) ──────────────────────────────────
  TIERS: { big: 5, mega: 15, epic: 40, legendary: 100 },

  // ── Hunger and moods (docs/04 §12) ─────────────────────
  HUNGER_MAX: 100,
  HUNGER_PER_SPIN: 2,
  HUNGER_PER_RECKONING_SPIN: 5,
  HUNGER_CRUMBS: 5,
  MOOD_PECKISH: 40,
  MOOD_RAVENOUS: 70,
  PECKISH_LUCK: -1,
  RAVENOUS_XMULT: 1.5,
  RAVENOUS_EAT_CHANCE: 0.1,
  STARVING_XMULT: 2,
  AMUSED_SPINS: 3,
  AMUSED_LUCK: 2,
  SULK_NUDGES: 4,
  SULK_HOLD_COST: 2,
  ADORING_WINDOWS: 3,
  /** The machine never eats or is fed below this many deck symbols. */
  MIN_DECK: 8,

  // ── Feeding (docs/04 §9) ───────────────────────────────
  FEED_HUNGER: { C: 15, U: 25, R: 40, L: 70 } satisfies Record<Rarity, number>,
  FEED_COINS_PRICE: 0.2,
  FEED_COINS_HUNGER: 20,

  // ── Rot (docs/04 §11) ──────────────────────────────────
  ROT_AGE: 6,
  COMPOST_CHANCE: 0.3,

  // ── Symbols ────────────────────────────────────────────
  CANDLE_MELT: 5,
  WORM_TURN: 4,
  MAGGOT_MEAL: 2,
  CAT_MEAL: 3,
  FLY_SPAWN_CHANCE: 0.25,
  WAX_RELIGHT: 3,
  EGG_HATCH: 6,
  SEED_GROW: 3,
  HOURGLASS_MAX: 2,
  SIGILS_TO_TRIGGER: 3,
  /**
   * Each bonus mode plays at most this many times per window. Bonus prizes scale with par, so a
   * deck stuffed with sigils would otherwise out-earn every tribute forever (balance pass 2).
   */
  BONUS_PER_WINDOW: 1,
  /** Par factor per bonus already played this run (bonus prizes can't carry a run forever). */
  BONUS_FATIGUE: 0.98,

  // ── Scratch of Fate ───────────────────────────────────
  SCRATCH_PRIZES: { p2: 2, p5: 5, p10: 10, p25: 25, jackpot: 100 },
  SCRATCH_WIN_WEIGHTS: { none: 30, p2: 30, p5: 22, p10: 11, p25: 5, jackpot: 2 },
  SCRATCH_TRAP_VOID: 0.7,
  SCRATCH_PLUG_CHANCE: 0.03,
  SCRATCH_REVEAL_COUNTS: 0.5,

  // ── Reckonings (docs/04 §16, docs/05 §5) ──────────────
  RECKONING_EVERY: 5,
  RECKONING_SOULS: 3,
  LAST_SUPPER_N: 40,
  LAST_SUPPER_TRIBUTE_MULT: 2,
  ENDLESS_RECKONING_EVERY: 3,
  DEBT_RELIEF_MULT: 1.3,
  WIDOW_LUCK_CAP: 3,
  STOPWATCH_SPINS: 0.7,
  STOPWATCH_PAY_EARLY: 3,
  FIRST_COLLECTOR: 'auditor',

  // ── Hexes (docs/04 §13) ───────────────────────────────
  HEX_DURATION: 3,
  /** Hex VALUE (docs/05 §1.5 lists 0; raised so a CLEANSE line actually pays). */
  HEX_VALUE: 2,
  HEX_MAX: 3,
  CLEANSE_MULT: 5,
  CLEANSE_MIN: 3,
  HEX_ADDICT_CLEANSE: 15,
  HEX_BAD_LUCK: -2,
  HEX_TAX: 0.8,
  HEX_ROT_AGE: 2,
  HEX_PANG: 3,

  // ── Propositions (docs/04 §15, docs/05 §7) ────────────
  DEAL_EVERY: 3,
  COVENANT_DEALS: 13,
  PHONE_DEALS: 13,
  GOOD_PATH_DEALS: 5,
  PATH_RELIC_DEALS: 7,
  REFUSE_HABIT: 10,
  BLOOD_NUDGE_COST: 0.02,
  BORROWED_LUCK_EVERY: 5,
  /** Twin: copies of the best symbol (and others destroyed). 3 → 2 in balance pass 2 (thin-deck runaways). */
  TWIN_COPIES: 2,
  TITHE: 0.25,
  LOAN_TRIBUTE_MULT: 1.6,
  LONG_NIGHT_SPINS: 5,
  BITTER_PILL_WINDOWS: 5,
  GRIN_HUNGER_FLOOR: 30,

  // ── Double or Devoured (docs/04 §17.2) ────────────────
  HILO_LADDER: [2, 3, 5, 10, 25, 100],
  /**
   * MAW cards in the shoe per rung (on top of the 13 ranks). A MAW card devours whatever you
   * guessed. Without them, seeing the current card makes every rung +EV (~77% per guess); with
   * them each step is roughly break-even and the Devil's rung is a ~1-in-75 thrill.
   */
  HILO_MAW: [3, 4, 5, 7, 10, 20],
  HILO_SIGIL_BASE: 10,
  HILO_DEVOUR_HUNGER: 20,
  HILO_HIGH: 7,

  // ── Wheel of Fate + Night Shift (docs/04 §17.3) ───────
  /** Segments per ring (equal size). cN = N par, nN = Night Shift N spins. */
  WHEEL_RINGS: [
    ['c3', 'c5', 'c8', 'step', 'c3', 'c12', 'n5', 'c5', 'c20', 'step'],
    ['c25', 'c35', 'n10', 'c45', 'step', 'c25', 'charm', 'c60'],
    ['c150', 'shard', 'c150', 'legendary', 'c150', 'plug'],
  ],
  /** Rough par value per prize, used to "keep the better" on a well-timed stop and by the auto-resolver. */
  WHEEL_PRIZE_RANK: { step0: 40, step1: 180, charm: 40, shard: 120, legendary: 300, plug: 500, night: 3 } as Record<string, number>,
  WHEEL_AUTO_HIT: 0.4,
  NIGHT_SHIFT_RETRIGGER: 3,
  /** Retriggers stop once a window's Night Shifts have added this many spins. */
  NIGHT_SHIFT_RETRIGGER_CAP: 15,

  // ── Relics, Molt and Teeth (docs/04 §20) ──────────────
  RELIC_SLOTS_BASE: 3,
  RELIC_SLOT_COSTS: [10, 25, 60],
  SHARDS_PER_RELIC: 3,
  MOLT_MIN_TRIBUTE: 15,
  TEETH_OFFSET: 10,
  TEETH_EXP: 1.6,
  FORMS: ['brass', 'bone', 'flesh', 'gilded'] as const,
  FORM_LUCK: 1,
  BONE_TOOTH_PCT: 0.5,
  FLESH_HUNGER_X: 0.8,
  GILDED_JACKPOT_X: 2,
  DEED_TRIBUTE_ADD: 5,
  START_TRIBUTE_MAX: 10,
  SAINT_MIRACLE: 0.25,
  JAR_DEVOUR_KEEP: 0.5,
  JAR_DEVOUR_XMULT: 0.2,

  // ── Eyes, Doom and Devour (docs/04 §14) ───────────────
  DOOM_MAX: 6,
  /** Eyes visible for NOTICED (Doom +1). */
  NOTICED_EYES: 3,
  /** Devour chance per Doom point per spin. */
  DEVOUR_PER_DOOM: 0.015,
  DEVOUR_HUNGER: 30,
  MIRACLE_X: 10,
  GOLDEN_DEVOUR_X: 2,
  /** Doom decays at window end when no Eye was seen in the last this-many spins. */
  DOOM_QUIET_SPINS: 3,
  MOON_DOOM: 3,
  // Achievements (docs/05 §9)
  ACH_IRON_WRIST: 100,
  ACH_SURGEON: 50,
  ACH_WELL_FED: 5,
  ACH_SCRATCH_MASTER: 0.8,
  ACH_INSATIABLE: 60,
  /** Minutes of stillness that count as a Hunger Strike at the Last Supper, and the idle lines. */
  HUNGER_STRIKE_MIN: 3,
  IDLE_LINE_MIN: 2,
  ASLEEP_MIN: 10,
  /** Doom at which the Maw's lines sometimes glitch into lowercase, and how often. */
  DOOM_GLITCH: 4,
  DOOM_GLITCH_CHANCE: 0.25,
  DEVOUR_TEETH: 3,
  PLINKO_BALLS: 3,
  /** Buckets left→right (par, or 'chomp': nothing, Hunger −10). */
  PLINKO_BUCKETS: ['3', 'chomp', '5', '10', '50', '10', '5', 'chomp', '3'],
  /** Peg rows; each bounces the ball half a bucket left or right. */
  PLINKO_ROWS: 16,
  PLINKO_CHOMP_HUNGER: 10,
  /** Golden pegs for Golden Teeth (row, position in half-buckets). */
  PLINKO_GOLDEN: [
    [5, 7],
    [9, 11],
  ] as [number, number][],
  PLINKO_PLUG_CHANCE: 0.3,
  FEAST_RESPINS: 3,
  /** Chance an empty cell lands a morsel on a respin. */
  FEAST_LAND: 0.05,
  FEAST_KINDS: { morsel: 88, fork: 3, pepper: 3, appetizer: 4, main: 1.5, dessert: 0.5 } as Record<string, number>,
  FEAST_MORSEL_VALUES: [1, 2, 3, 5],
  FEAST_VALUES: { appetizer: 25, main: 100, dessert: 500 } as Record<string, number>,
  FEAST_FULL: 1000,
  FEAST_PLUG_CHANCE: 0.05,

  // ── Web stat nodes ────────────────────────────────────
  SERENDIPITY: 0.05,
  DESTINY_FATE_CAP: 45,
  MIDAS: 0.1,

  BLOOD_BANK_X: 0.1,
  BONESAW_X: 0.1,
  INF_RECEIPT_CARRY: 1.25,
  HEART_MACHINE_CAP: 60,
  SEVENTH_SEAL_X: 1.3,
  BLOODNAIL_COST: 0.01,
  HISTORY_LENGTH: 20,

  PLUG_PARTS: ['plug_1', 'plug_2', 'plug_3', 'plug_4', 'plug_5'],

  // ── Ledger pages (docs/06 §5) ─────────────────────────
  LEDGER_PAGE_CHANCE: 0.1,

  // ── The Hand (docs/04 §21) ────────────────────────────
  HAND_INTERVALS: [1.6, 1.2, 0.9, 0.6, 0.45, 0.35],
  HAND_PAY_AT: 1.5,
  HAND_FEED_AT: 60,

  /** Feel guardrail (docs/10 §5): max share of seeds with < 3 wins in the first 10 spins. */
  GUARDRAIL_FIRST10_MAX_FAIL: 0.01,

  // ── Dialogue ───────────────────────────────────────────
  DIALOGUE_NO_REPEAT: 20,
  DIALOGUE_SILENCE: 0.3,

  // ── Presentation-side timing (render reads these; sim never does) ──
  AUTOSAVE_THROTTLE_MS: 2000,
  REEL_STAGGER_MS: 110,
  PARTICLE_CAP: 400,
} as const;

export type Balance = typeof B;

/** Every numeric tunable, for text that quotes a number (`{GOLDEN_DEVOUR_X}` in i18n strings). */
export const B_PARAMS: Record<string, number> = Object.fromEntries(
  (Object.entries(B) as [string, unknown][]).filter((e): e is [string, number] => typeof e[1] === 'number'),
);
